import React from "react";
import type {
  Vehicle,
  VehicleDetailViewProps,
  VehicleTrimDetailTableRow
} from "@dealer-center/inventory-pages-gvm-interface";
import type {
  InterstateOnChangeEvent,
  TextInputEventValue,
  TypeSafeTableColumns
} from "@dealer-center/core-components";
import {
  IconButton,
  Table,
  TextInput,
  TrashIcon
} from "@dealer-center/core-components";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";
import { modelMetadata } from "@dealer-center/inventory-core-utils";

export const VehicleDetailView = ({
  isLoading,
  vehicleViewData,
  vehicleToSave,
  setVehicleToSave
}: VehicleDetailViewProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();

  const getVehicleDetailColumns =
    (): TypeSafeTableColumns<VehicleTrimDetailTableRow> => {
      return [
        {
          key: "property",
          dataIndex: "property",
          title: "",
          columnSortable: false,
          width: 200
        },
        {
          key: "sourceItem",
          dataIndex: "sourceItem",
          title: vehicleViewData.viewItem.thirdPartyId,
          columnSortable: false
        },
        {
          key: "override",
          dataIndex: "overrideInput",
          title:
            (vehicleViewData.qa ? "QA Item" : "Live Item") +
            " (" +
            vehicleViewData.editItem.thirdPartyId +
            ")",
          columnSortable: false
        },
        {
          key: "resetButton",
          dataIndex: "resetButton",
          title: "",
          columnSortable: false,
          width: 10
        },
        {
          key: "overrideSource",
          dataIndex: "overrideSource",
          title: "",
          columnSortable: false,
          width: 200
        }
      ];
    };

  const handleInputChange = (
    e: InterstateOnChangeEvent<TextInputEventValue>
  ) => {
    const { name = "", value } = e.target;
    if (value === vehicleToSave[name]) return;
    setVehicleToSave({ ...vehicleToSave, [name]: value });
  };

  const renderInput = (field: string) => {
    return (
      <TextInput
        disabled={!isGvmRwAccessUser}
        maxLength={modelMetadata.length(field, 20)}
        name={field}
        required="requiredNoAnnotation"
        type={modelMetadata.isNumeric(field) ? "number" : "text"}
        value={vehicleToSave[field]}
        onChange={handleInputChange}
      />
    );
  };

  const renderResetButton = (field: string, viewItemValue: string) => {
    if (isGvmRwAccessUser && vehicleToSave[field] !== viewItemValue) {
      const handleReset = () => {
        setVehicleToSave({ ...vehicleToSave, [field]: viewItemValue });
      };

      return (
        <IconButton buttonStyle="tertiary" size="md" onClick={handleReset}>
          <TrashIcon title="Reset" />
        </IconButton>
      );
    }

    return undefined;
  };

  const getVehicleDetailData = (): VehicleTrimDetailTableRow[] => {
    const tableRows: VehicleTrimDetailTableRow[] = [];

    const originalItem = vehicleViewData.originalItem;
    const editItem = vehicleViewData.editItem;
    const viewItem = vehicleViewData.viewItem;
    const isQaRecordPresent = vehicleViewData.qa;

    for (const key in vehicleViewData.validFields) {
      let overrideSource = "";
      const viewValue = viewItem[key as keyof Vehicle] ?? "";
      const editValue = editItem[key as keyof Vehicle] ?? "";
      const originalValue = originalItem[key as keyof Vehicle] ?? "";

      if (
        isQaRecordPresent &&
        (originalValue !== viewValue || originalItem === viewItem)
      ) {
        overrideSource = "QA Change";
      } else if (originalValue !== editValue) {
        overrideSource = "DDC Change";
      }

      tableRows.push({
        property: vehicleViewData.validFields[key],
        sourceItem: viewValue,
        overrideInput: renderInput(key),
        resetButton: renderResetButton(key, String(viewValue)),
        overrideSource
      });
    }

    return tableRows;
  };

  const render = () => {
    return (
      <Table.Table
        columns={getVehicleDetailColumns()}
        data={getVehicleDetailData()}
        data-testid={"gvm-trim-results"}
        dataDensity="small"
        enablePagination={false}
        isLoading={isLoading}
        sortableColumns={true}
        tableLayout={"auto"}
      />
    );
  };

  return render() || null;
};
